import React, { createContext, useState, useContext, ReactNode, Dispatch, SetStateAction } from 'react';
import { identity } from 'lodash';

// Utils
import useLocalStorage from '../../../../utils/services/localStorage/useLocalStorage';
import globalLogger from '../../../../logger';
import useProductFetching from './useCheckoutProductFetching';
import useCheckoutNavigationStack from './useCheckoutNavigationStack';

// Components
import CheckoutErrorModal from '../error/CheckoutErrorModal';

// Constants
import { CHECKOUT_SELECTED_PRICE_ID_KEY, CHECKOUT_SELECTED_PRODUCT_ID_KEY } from '../checkoutConstants';

// Types
import { CheckoutPrice, CheckoutProduct } from '../../../../../common/payment/products/productTypes';
import { MNSubscription } from '../../../../../common/payment/subscriptions/subscriptionTypes';
import { ImMap } from '../../../../../common/utils/immutableHelper';
import { ImMNUser } from '../../../../../common/users/userModelTypes';

const logger = globalLogger.createChannel('checkout');

type CheckoutContextProviderProps = {
    children: ReactNode;
    subscription: ImMap<MNSubscription> | undefined;
    currentUser: ImMNUser;
    location: {
        pathname: string;
    };
    dispatchOpenIntercomMessages: () => void;
    dispatchFetchSubscriptionDetails: () => void;
    dispatchNavigateTo: (url: string) => void;
    dispatchNavigateBrowserBack: () => void;
};

type CheckoutContextArgs = {
    products: CheckoutProduct[];
    selectedPriceId: string | undefined;
    selectedProductId: string | undefined;
    selectedProduct?: CheckoutProduct;
    selectedPrice?: CheckoutPrice;
    addMemberIds: string[];
    removeMemberIds: string[];
    initialMemberIds: string[];
    quantity?: number;
    selectedSeatCount?: number;
    subscription: ImMap<MNSubscription> | undefined;
    currentUser: ImMNUser;
    setSelectedProductId: Dispatch<SetStateAction<string | undefined>>;
    setInitialMemberIds: Dispatch<SetStateAction<string[]>>;
    setSelectedPriceId: Dispatch<SetStateAction<string | undefined>>;
    setSelectedSeatCount: Dispatch<SetStateAction<number | undefined>>;
    setAddMemberIds: Dispatch<SetStateAction<string[]>>;
    setRemoveMemberIds: Dispatch<SetStateAction<string[]>>;
    setQuantity: Dispatch<SetStateAction<number>>;
    dispatchOpenIntercomMessages: () => void;
    dispatchFetchSubscriptionDetails: () => void;
    dispatchNavigateTo: (url: string) => void;
    dispatchNavigateBack: () => void;
};

// Create a context
const CheckoutContext = createContext<CheckoutContextArgs>({
    selectedProductId: '',
    selectedPriceId: '',
    selectedProduct: undefined,
    selectedPrice: undefined,
    addMemberIds: [],
    removeMemberIds: [],
    initialMemberIds: [],
    products: [],

    setSelectedProductId: identity,
    setSelectedPriceId: identity,
    setSelectedSeatCount: identity,
    setAddMemberIds: identity,
    setRemoveMemberIds: identity,
    setQuantity: identity,
    setInitialMemberIds: identity,

    // stuff from redux
    subscription: undefined,
    currentUser: {} as ImMNUser, // this will be replaced by the actual user as soon as the context is initialized
    dispatchOpenIntercomMessages: identity,
    dispatchFetchSubscriptionDetails: identity,
    dispatchNavigateTo: identity,
    dispatchNavigateBack: identity,
});

// Create a provider for components to consume and access the state
export const CheckoutContextProvider = ({
    children,
    subscription,
    currentUser,
    location,
    dispatchOpenIntercomMessages,
    dispatchFetchSubscriptionDetails,
    dispatchNavigateTo,
    dispatchNavigateBrowserBack,
}: CheckoutContextProviderProps) => {
    const [selectedProductId, setSelectedProductId] = useLocalStorage<string | undefined>(
        CHECKOUT_SELECTED_PRODUCT_ID_KEY,
        undefined,
    );

    const [selectedPriceId, setSelectedPriceId] = useLocalStorage<string | undefined>(
        CHECKOUT_SELECTED_PRICE_ID_KEY,
        undefined,
    );

    const [addMemberIds, setAddMemberIds] = useState<string[]>([]);
    const [removeMemberIds, setRemoveMemberIds] = useState<string[]>([]);
    const [initialMemberIds, setInitialMemberIds] = useState<string[]>([]);

    const [quantity, setQuantity] = useState(1);
    const [selectedSeatCount, setSelectedSeatCount] = useState<number | undefined>(undefined);

    const [products, productsFetchError] = useProductFetching(
        selectedProductId,
        setSelectedProductId,
        setSelectedPriceId,
    );

    const navigationStack = useCheckoutNavigationStack(location);

    const dispatchNavigateBack = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_, ...rest] = navigationStack;
        if (rest.length > 0) {
            dispatchNavigateTo(rest[0]);
        } else {
            dispatchNavigateBrowserBack();
        }
    };

    const selectedProduct = products.find((product) => product._id === selectedProductId);
    const selectedPrice = selectedProduct?.prices.find((price) => price._id === selectedPriceId);

    const contextValue = {
        products,
        selectedProduct,
        selectedPrice,
        selectedPriceId,
        selectedProductId,
        addMemberIds,
        removeMemberIds,
        initialMemberIds,
        setInitialMemberIds,
        quantity,
        selectedSeatCount,

        subscription,
        currentUser,

        setSelectedPriceId,
        setSelectedProductId,
        setAddMemberIds,
        setRemoveMemberIds,
        setSelectedSeatCount,
        setQuantity,

        dispatchOpenIntercomMessages,
        dispatchFetchSubscriptionDetails,
        dispatchNavigateTo,
        dispatchNavigateBack,
    };

    logger.debug('CheckoutContext', {
        selectedPriceId,
        selectedProductId,
        products,
    });

    return (
        <CheckoutContext.Provider value={contextValue}>
            {productsFetchError ? <CheckoutErrorModal error={productsFetchError} /> : children}
        </CheckoutContext.Provider>
    );
};

export const useCheckoutContext = () => useContext(CheckoutContext);
